import { matchPath } from 'react-router-dom';
import { appRoutes } from '../../appRoutes';
import { authRoutes } from '../../authRoutes';
import { ROUTE_LABELS } from '../constants/constants';
import { RouteType } from '../nav/AppRoute';
import { APPLICATIONS } from '../nav/apps';

/**
 * A cached array of unique route patterns.
 * Initialized to null to signify that the patterns have not yet been extracted and calculated.
 */
let cachedRoutePatterns: string[] | null = null;

/**
 * Recursively extracts all path patterns from a route tree.
 * * @param route - The route object to process.
 * @returns An array of strings containing all path patterns found in the route and its children.
 */
const extractPatterns = (route: RouteType): string[] => {
    const currentPath = route.path ? [route.path] : [];

    if (!route.items) {
        return currentPath;
    }

    const childPatterns = route.items.reduce((acc, item) => acc.concat(extractPatterns(item)), [] as string[]);

    return currentPath.concat(childPatterns);
};

/**
 * Retrieves a unique, flattened list of all defined path patterns from the application's
 * route configurations (`authRoutes` and `appRoutes`) using a pure, recursive approach.
 * * @remarks
 * Utilizes a caching mechanism and the pure function `extractPatternsPure`.
 * * @returns A unique array of strings representing all route path patterns.
 */
const getRoutePatterns = (): string[] => {
    if (cachedRoutePatterns) return cachedRoutePatterns;

    try {
        const allRoutes = [...authRoutes, ...appRoutes];

        const allPatterns = allRoutes.flatMap(extractPatterns);

        cachedRoutePatterns = Array.from(new Set(allPatterns));

        return cachedRoutePatterns;
    } catch (error) {
        // Routes not yet initialized, return empty array
        // This can happen when Faro initializes before routes are fully loaded
        return [];
    }
};

/**
 * Finds the first route pattern that matches the given URL pathname.
 * * @remarks
 * This function is used for transforming a dynamic URL (like `/user/123/details`) into
 * its static, human-readable pattern (like `/user/:id/details`) for logging or analytics.
 * It relies on the cached list of patterns returned by `getRoutePatterns` and an external
 * `matchPath` utility.
 * * @param pathname - The current URL pathname to check against the known route patterns
 * (e.g., `/planning/workspace/01.../resource/01.../details`).
 * @returns The first matching route pattern string found (e.g., `/planning/workspace/:wid/resource/:rid/details`),
 * or `null` if no pattern matches the pathname.
 */

export const getMatchingRoutePattern = (pathname: string): string | null => {
    const patterns = getRoutePatterns();

    for (const pattern of patterns) {
        if (matchPath(pattern, pathname)) return pattern;
    }

    return null;
};

/**
 * Generates a human-readable page name from a route pattern.
 *
 * This function converts a URL route pattern into a formatted page name.
 * It handles root paths, single-segment paths, and multi-segment paths.
 *
 * Rules:
 * - If the pattern is `'/'`, returns `ROUTE_LABELS.home`.
 * - Splits the path into segments and ignores empty segments and parameters (segments starting with `':'`).
 * - For certain single-segment paths (those whose pattern matches an application's `home`),
 *   the segment is capitalized and `ROUTE_LABELS.home` is appended.
 *   Example: if an application's `home` is `'/dashboard'`, then:
 *     `'/dashboard'` → `'DashboardHome'`.
 * - For other single-segment or multi-segment paths, each segment is
 *   capitalized and concatenated.
 *   Example: `'/user/profile'` → `'UserProfile'`.
 *
 * @param pattern - The route pattern string (e.g., `'/dashboard'`, `'/user/:id'`).
 * @returns The formatted page name as a string.
 */

const formatSegment = (seg: string) =>
    seg
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');

export function getPageNameFromRoutePattern(pattern: string): string {
    if (pattern === '/') return ROUTE_LABELS.home;

    // Split path into segments and filter out empty strings and params
    const segments = pattern
        .split('/')
        .filter(Boolean)
        .filter((seg) => !seg.startsWith(':'));

    if (segments.length === 0) return ROUTE_LABELS.home;

    // Single segment -> append 'Home' for routes like '/dashboard', '/supplier', etc.
    if (segments.length === 1 && APPLICATIONS.some((app) => app.home === pattern)) {
        return `${formatSegment(segments[0])}${ROUTE_LABELS.home}`;
    }

    return segments.map(formatSegment).join('');
}
